import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { LeaderboardEntry } from './api';

/**
 * ISO-8601 week id like "2026-W28" (UTC, Monday-based). MUST stay in sync with
 * `isoWeekId()` in functions/src/callable/shared.ts — the server keys the
 * weekly rows by this exact string.
 */
export function weekId(d = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = date.getUTCDay() || 7; // Mon=1 … Sun=7
  date.setUTCDate(date.getUTCDate() + 4 - day); // shift to this week's Thursday
  const year = date.getUTCFullYear();
  const week = Math.ceil(((date.getTime() - Date.UTC(year, 0, 1)) / 86400000 + 1) / 7);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

function toEntry(id: string, x: DocumentData): LeaderboardEntry {
  return {
    uid: id,
    name: typeof x.name === 'string' && x.name ? x.name : 'Runner',
    games: typeof x.games === 'number' ? x.games : 0,
    mvp: typeof x.mvp === 'number' ? x.mvp : 0,
    country: typeof x.country === 'string' ? x.country : null,
  };
}

/**
 * Top players by MVP wins, then games played. Read directly from Firestore
 * (public per the rules); the docs are written server-side and hold only a
 * display name, counts, and a cosmetic country code — never any private data.
 */
export async function fetchLeaderboard(top = 10): Promise<LeaderboardEntry[]> {
  const q = query(
    collection(db, 'leaderboard'),
    orderBy('mvp', 'desc'),
    orderBy('games', 'desc'),
    limit(top),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => toEntry(d.data().uid ?? d.id, d.data()));
}

/** Same board, but only this week's counters — resets every Monday (UTC). */
export async function fetchWeeklyLeaderboard(top = 10): Promise<LeaderboardEntry[]> {
  const q = query(
    collection(db, 'leaderboardWeekly'),
    where('week', '==', weekId()),
    orderBy('mvp', 'desc'),
    orderBy('games', 'desc'),
    limit(top),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => toEntry(d.data().uid ?? d.id, d.data()));
}
